const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://test-alb-1-25465940.us-east-1.elb.amazonaws.com';
const ADMIN_API_BASE_URL = process.env.REACT_APP_ADMIN_API_BASE_URL || 'http://54.210.59.238:8000/api/v1'; // dev default; prod sets REACT_APP_ADMIN_API_BASE_URL=/api/v1
export const THEME_MODE = process.env.REACT_APP_THEME_MODE || 'dev';

// const API_BASE_URL = 'http://localhost:8000';
// const ADMIN_API_BASE_URL = 'http://localhost:8000/api/v1';
const SCHEDULER_API_BASE_URL = ADMIN_API_BASE_URL;
const MOCK_GENIUS_API_BASE_URL = process.env.REACT_APP_MOCK_GENIUS_API_BASE_URL || 'http://100.56.161.246:5050';
const ADMIN_TOKEN_KEY = 'homestream_admin_token';
const AUTH_EXPIRED_EVENT = 'homestream-admin-auth-expired';

// Simple client-side invoke cooldowns to prevent rapid repeat calls.
const invokeGuards: Record<string, number> = {};
function checkCooldown(key: string, cooldownMs: number): string | null {
  const now = Date.now();
  const last = invokeGuards[key] ?? 0;
  if (now - last < cooldownMs) {
    const waitSeconds = Math.ceil((cooldownMs - (now - last)) / 1000);
    return `Check if the service is up and running before invoking again (wait ${waitSeconds}s).`;
  }
  invokeGuards[key] = now;
  return null;
}

function getAuthToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(ADMIN_TOKEN_KEY);
}

function buildAuthHeaders(headers?: HeadersInit): Headers {
  const merged = new Headers(headers || {});
  const token = getAuthToken();
  if (token && !merged.has('Authorization')) {
    merged.set('Authorization', `Bearer ${token}`);
  }
  return merged;
}

async function fetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const requestUrl = typeof input === 'string' ? input : input.toString();
  const headers = buildAuthHeaders(init.headers);
  const response = await globalThis.fetch(input, {
    ...init,
    headers,
  });

  const hasToken = typeof window !== 'undefined' && window.localStorage.getItem(ADMIN_TOKEN_KEY);
  const isAuthLoginRequest = requestUrl.includes('/auth/login');
  if (response.status === 401 && hasToken && !isAuthLoginRequest && typeof window !== 'undefined') {
    window.localStorage.removeItem(ADMIN_TOKEN_KEY);
    window.dispatchEvent(
      new CustomEvent(AUTH_EXPIRED_EVENT, {
        detail: {
          message: 'Session is over! Please login again.',
        },
      })
    );
  }

  return response;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface AdminLoginResponse {
  access_token: string;
  token_type: 'bearer';
  expires_in: number;
  admin_email: string;
}

export interface AdminMeResponse {
  authenticated: boolean;
  admin_email: string;
}

export interface ImageSearchRequest {
  query_image: File;
  game_name: string;
  user_clicked_time: string;
  userID: string;
  game_type: string;
  top_k?: number;
  user_latency?: number;
}

export interface HealthStatus {
  status: string;
  [key: string]: unknown;
}

export interface SetManualSyncGapRequest {
  game_name: string;
  manual_sync_time_gap: number;
  sync_calculation_latency?: number;
  top_k?: number;
  app_type?: 'dev' | 'prod';
}

export interface RecentRequest {
  id: number;
  game_name: string;
  user_id: string;
  timestamp: string;
  processing_time_seconds: number;
  sync_time_gap_seconds: number;
  manual_sync_time_gap?: number;
  visualization_url: string;
  user_query_url?: string;
  user_clicked_time: string;
  success: boolean;
  user_query_scoreboard_info?: Record<string, string>;
  matched_frame_data?: Record<string, string> | null;
  created_at: string;
}

export interface RecentRequestsResponse {
  requests: RecentRequest[];
  count: number;
}

// ============================================
// LIVE URL GAME INDEXING TYPES
// ============================================

export interface GameSetupRequest {
  game_name: string;
  frames_per_second: number;
}

export interface GameSetupResponse {
  success: boolean;
  message: string;
  game_name?: string;
  table_name?: string;
  frames_per_second?: number;
  setup_time?: string;
  error?: string;
}

export interface StartIndexingRequest {
  video_url: string;
  game_name: string;
  video_start_timestamp?: string; // HH:MM:SS, default "00:00:00"
  game_type?: string; // default "football"
  crop_height_ratio?: number; // 0.0-1.0, default 0.1
}

export interface StartIndexingResponse {
  success: boolean;
  message: string;
  game_name?: string;
  video_url?: string;
  task_id?: string;
  status?: string;
  error?: string;
}

export interface LastFrameResponse {
  success: boolean;
  message: string;
  game_name?: string;
  frame_number?: number;
  frame_path?: string;
  video_timestamp?: string;
  timestamp_seconds?: number;
  scoreboard_details?: Record<string, unknown>;
  scoreboard_confidence?: number;
  team_a?: string;
  team_b?: string;
  game_time?: string;
  period?: string;
  play_clock?: string;
  is_processed_frame?: boolean;
  created_at?: string;
  total_frames?: number;
  processed_frames?: number;
  error?: string;
  last_frame_path_url?: string;
  last_frames?: unknown[];
}

export interface GameResetRequest {
  game_name: string;
  frames_per_second: number;
}

export interface GameResetResponse {
  success: boolean;
  message: string;
  game_name?: string;
  table_name?: string;
  frames_per_second?: number;
  reset_time?: string;
  error?: string;
}

export interface InvalidateAudioUrlResponse {
  status: string;
  new_url: string;
  service_status: string;
  segments: string;
  cloudfront_invalidation_id: string;
}

// ============================================
// API RESPONSE HANDLER
// ============================================

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  try {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      return {
        success: false,
        error: typeof detail === 'string' ? detail : JSON.stringify(detail),
      };
    }
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

export const authApi = {
  async login(email: string, password: string): Promise<ApiResponse<AdminLoginResponse>> {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await globalThis.fetch(`${ADMIN_API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });
    return handleResponse(response);
  },

  async me(): Promise<ApiResponse<AdminMeResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/auth/me`);
    return handleResponse(response);
  },
};

// ============================================
// IMAGE MATCHING APIs
// ============================================

export const imageMatchingApi = {
  /**
   * POST /image-matching/image_search
   * Test the image matching logic for sync detection
   */
  async imageSearch(params: ImageSearchRequest): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('query_image', params.query_image);
    formData.append('game_name', params.game_name);
    formData.append('user_clicked_time', params.user_clicked_time);
    formData.append('userID', params.userID);
    formData.append('game_type', params.game_type);
    if (params.top_k) formData.append('top_k', params.top_k.toString());
    if (params.user_latency) formData.append('user_latency', params.user_latency.toString());

    const response = await fetch(`${API_BASE_URL}/image-matching/image_search`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },

  /**
   * GET /image-matching/health
   * Health check for image indexing service
   */
  async health(): Promise<ApiResponse<HealthStatus>> {
    const response = await fetch(`${API_BASE_URL}/image-matching/health`);
    return handleResponse(response);
  },

  /**
   * GET /image-matching/recent-game-time/{game_name}
   * Check the last game-time that got registered/indexed
   */
  async getRecentGameTime(gameName: string): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/image-matching/recent-game-time/${encodeURIComponent(gameName)}`);
    return handleResponse(response);
  },

  /**
   * GET /image-matching/recent-requests
   * Get recent image matching requests (logs)
   */
  async getRecentRequests(gameName?: string, limit: number = 500): Promise<ApiResponse<RecentRequestsResponse>> {
    const params = new URLSearchParams();
    if (gameName) params.append('game_name', gameName);
    params.append('limit', limit.toString());
    const response = await fetch(`${API_BASE_URL}/image-matching/recent-requests?${params.toString()}`);
    return handleResponse(response);
  },

  /**
   * POST /image-matching/set_manual_sync_gap
   * Set manual sync time gap for a specific game
   */
  async setManualSyncGap(params: SetManualSyncGapRequest): Promise<ApiResponse> {
    const formData = new URLSearchParams();
    formData.append('game_name', params.game_name);
    formData.append('manual_sync_time_gap', params.manual_sync_time_gap.toString());
    if (params.sync_calculation_latency !== undefined) {
      formData.append('sync_calculation_latency', params.sync_calculation_latency.toString());
    }
    if (params.top_k !== undefined) {
      formData.append('top_k', params.top_k.toString());
    }
    formData.append('app_type', params.app_type || 'prod');

    const response = await fetch(`${API_BASE_URL}/image-matching/set_manual_sync_gap`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    });
    return handleResponse(response);
  },
};

// ============================================
// LIVE URL GAME INDEXING APIs
// ============================================

export const liveUrlGameIndexingApi = {
  /**
   * POST /live-url-game-indexing/setup
   * Setup all DBs, folders, etc. for a new game
   */
  async setup(params: GameSetupRequest): Promise<ApiResponse<GameSetupResponse>> {
    const formData = new FormData();
    formData.append('game_name', params.game_name);
    formData.append('frames_per_second', params.frames_per_second.toString());

    const response = await fetch(`${API_BASE_URL}/live-url-game-indexing/setup`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },

  /**
   * POST /live-url-game-indexing/start-indexing
   * Start indexing a game after setup is done
   */
  async startIndexing(params: StartIndexingRequest): Promise<ApiResponse<StartIndexingResponse>> {
    const formData = new FormData();
    formData.append('video_url', params.video_url);
    formData.append('game_name', params.game_name);
    if (params.video_start_timestamp) {
      formData.append('video_start_timestamp', params.video_start_timestamp);
    }
    if (params.game_type) {
      formData.append('game_type', params.game_type);
    }
    if (params.crop_height_ratio !== undefined) {
      formData.append('crop_height_ratio', params.crop_height_ratio.toString());
    }

    const response = await fetch(`${API_BASE_URL}/live-url-game-indexing/start-indexing`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },

  /**
   * GET /live-url-game-indexing/last-frame/{game_name}
   * Get last 10 frames details from DB
   */
  async getLastFrame(gameName: string): Promise<ApiResponse<LastFrameResponse>> {
    const response = await fetch(`${API_BASE_URL}/live-url-game-indexing/last-frame/${encodeURIComponent(gameName)}`);
    return handleResponse(response);
  },

  /**
   * POST /live-url-game-indexing/stop-processing/{game_name}
   * Stop asynchronous frame processing for a game
   */
  async stopProcessing(gameName: string): Promise<ApiResponse> {
    const response = await fetch(`${API_BASE_URL}/live-url-game-indexing/stop-processing/${encodeURIComponent(gameName)}`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  /**
   * POST /live-url-game-indexing/reset-game
   * Reset game indexing by dropping existing table and recreating it
   * NOTE: First call stopProcessing, then call this
   */
  async resetGame(params: GameResetRequest): Promise<ApiResponse<GameResetResponse>> {
    const formData = new FormData();
    formData.append('game_name', params.game_name);
    formData.append('frames_per_second', params.frames_per_second.toString());

    const response = await fetch(`${API_BASE_URL}/live-url-game-indexing/reset-game`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },

  /**
   * POST /api/v1/scheduler/invalidate-audio
   * Invalidates audio URL via backend proxy (avoids browser CORS issues)
   */
  async invalidateAudioUrl(audioUrl: string): Promise<ApiResponse<InvalidateAudioUrlResponse> & { cooldown?: boolean }> {
    const cooldownMsg = checkCooldown('invalidate-audio-url', 5 * 60 * 1000);
    if (cooldownMsg) {
      return { success: false, error: cooldownMsg, cooldown: true };
    }

    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/invalidate-audio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ audio_url: audioUrl }),
    });
    return handleResponse(response);
  },
};

// ============================================
// SCHEDULER TYPES
// ============================================

export interface ScheduleCreateRequest {
  game_name: string;
  video_url: string;
  scheduled_time: string; // ISO 8601 format
  game_type?: string;
  video_start_timestamp?: string;
  crop_height_ratio?: number;
  frames_per_second?: number;
  manual_sync_time_gap?: number;
  app_type?: 'dev' | 'prod';
}

export interface ScheduleResponse {
  success: boolean;
  schedule_name?: string;
  scheduled_time?: string;
  game_name?: string;
  arn?: string;
  error?: string;
}

export interface ScheduleDetail {
  name: string;
  state: string;
  schedule_expression?: string;
  game_name?: string;
  video_url?: string;
  game_type?: string;
  video_start_timestamp?: string;
  crop_height_ratio?: number;
  frames_per_second?: number;
  manual_sync_time_gap?: number;
  app_type?: string;
  scheduled_time?: string;
  created_at?: string;
}

export interface ScheduleListResponse {
  schedules: ScheduleDetail[];
  count: number;
}

export interface DeleteScheduleResponse {
  success: boolean;
  deleted?: string;
  error?: string;
}

export interface TriggerRequest {
  game_name: string;
  video_url: string;
  game_type?: string;
  video_start_timestamp?: string;
  crop_height_ratio?: number;
  frames_per_second?: number;
  manual_sync_time_gap?: number;
  app_type?: 'dev' | 'prod';
}

export interface TriggerResponse {
  success: boolean;
  execution_name?: string;
  execution_arn?: string;
  start_date?: string;
  game_name?: string;
  error?: string;
}

export interface ExecutionStatusResponse {
  success: boolean;
  execution_arn?: string;
  status?: string;
  start_date?: string;
  stop_date?: string;
  input?: string;
  output?: string;
  error?: string;
}

export interface ExecutionDetail {
  execution_arn: string;
  name: string;
  status: string;
  start_date?: string;
  stop_date?: string;
  duration?: string;
  game_name?: string;
}

export interface ExecutionListResponse {
  success: boolean;
  executions: ExecutionDetail[];
  count: number;
  error?: string;
}

export interface ExecutionStepDetail {
  step_name: string;
  status: string; // SUCCEEDED, FAILED, IN_PROGRESS, CANCELLED
  start_time?: string;
  end_time?: string;
  duration?: string;
  error?: string;
  cause?: string;
}

export interface ExecutionHistoryResponse {
  success: boolean;
  execution_arn?: string;
  steps: ExecutionStepDetail[];
  count: number;
  error?: string;
}

// ============================================
// SCHEDULER APIs
// ============================================

export const schedulerApi = {
  /**
   * POST /api/v1/scheduler/schedules
   * Create a new schedule for game indexing
   */
  async createSchedule(params: ScheduleCreateRequest): Promise<ApiResponse<ScheduleResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/schedules`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  /**
   * GET /api/v1/scheduler/schedules
   * List all scheduled games
   */
  async listSchedules(): Promise<ApiResponse<ScheduleListResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/schedules`);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/scheduler/schedules/{schedule_name}
   * Get details of a specific schedule
   */
  async getSchedule(scheduleName: string): Promise<ApiResponse<ScheduleDetail>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/schedules/${encodeURIComponent(scheduleName)}`);
    const result = await handleResponse<{ success: boolean; data: ScheduleDetail; message: string }>(response);
    // Backend returns { success, data: scheduleDetail, message }, extract the nested data
    if (result.success && result.data?.data) {
      return { success: true, data: result.data.data };
    }
    return { success: result.success, data: result.data?.data, error: result.error };
  },

  /**
   * DELETE /api/v1/scheduler/schedules/{schedule_name}
   * Delete a schedule
   */
  async deleteSchedule(scheduleName: string): Promise<ApiResponse<DeleteScheduleResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/schedules/${encodeURIComponent(scheduleName)}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  /**
   * POST /api/v1/scheduler/trigger
   * Directly trigger Step Functions immediately (Master API)
   */
  async triggerNow(params: TriggerRequest): Promise<ApiResponse<TriggerResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/trigger`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  /**
   * GET /api/v1/scheduler/executions/{execution_arn}
   * Get the status of a Step Functions execution
   */
  async getExecutionStatus(executionArn: string): Promise<ApiResponse<ExecutionStatusResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/executions/${encodeURIComponent(executionArn)}`);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/scheduler/executions
   * List all Step Functions executions
   */
  async listExecutions(status?: string, maxResults: number = 100): Promise<ApiResponse<ExecutionListResponse>> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('max_results', maxResults.toString());
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/executions?${params.toString()}`);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/scheduler/execution-history?execution_arn=...
   * Get step-by-step execution history
   */
  async getExecutionHistory(executionArn: string): Promise<ApiResponse<ExecutionHistoryResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/scheduler/execution-history?execution_arn=${encodeURIComponent(executionArn)}`);
    return handleResponse(response);
  },
};

// ============================================
// GAMES TYPES
// ============================================

export interface GameCreate {
  home_team: string;
  home_team_id?: string;
  away_team: string;
  away_team_id?: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM (24-hour)
  timezone: string; // UTC
  venue: string;
  network: string;
  season_tag?: string;
  audio_url?: string;
  home_team_logo_url?: string;
  away_team_logo_url?: string;
  // Football-specific
  home_football_logo_right_url?: string;
  home_football_logo_left_url?: string;
  away_football_logo_right_url?: string;
  away_football_logo_left_url?: string;
  // Basketball-specific
  home_basketball_logo_url?: string;
  away_basketball_logo_url?: string;
  home_team_commentator?: string;
  away_team_commentator?: string;
  home_team_school_name?: string;
  away_team_school_name?: string;
  home_team_catch_phrase?: string;
  away_team_catch_phrase?: string;
  home_team_theme_color?: string;
  away_team_theme_color?: string;
  home_audio_url?: string;
  away_audio_url?: string;
  bot_id: string;
  game_type: 'basketball' | 'football';
}

// home_team/away_team (and their IDs) are intentionally omitted — the backend
// does not allow changing a game's matchup once it's created.
export interface GameUpdate {
  date?: string;
  time?: string;
  timezone?: string;
  venue?: string;
  network?: string;
  season_tag?: string;
  audio_url?: string;
  home_team_logo_url?: string;
  away_team_logo_url?: string;
  home_football_logo_right_url?: string;
  home_football_logo_left_url?: string;
  away_football_logo_right_url?: string;
  away_football_logo_left_url?: string;
  home_basketball_logo_url?: string;
  away_basketball_logo_url?: string;
  home_team_commentator?: string;
  away_team_commentator?: string;
  home_team_school_name?: string;
  away_team_school_name?: string;
  home_team_catch_phrase?: string;
  away_team_catch_phrase?: string;
  home_team_theme_color?: string;
  away_team_theme_color?: string;
  home_audio_url?: string;
  away_audio_url?: string;
  bot_id?: string;
  game_type?: 'basketball' | 'football';
  status?: 'unscheduled' | 'scheduled' | 'revoked';
}

export interface Game {
  game_id: string;
  game_name: string;
  home_team: string;
  home_team_id?: string;
  away_team: string;
  away_team_id?: string;
  date: string;
  time: string;
  timezone: string;
  venue: string;
  network: string;
  season_tag?: string;
  audio_url?: string;
  home_team_logo_url?: string;
  away_team_logo_url?: string;
  home_football_logo_right_url?: string;
  home_football_logo_left_url?: string;
  away_football_logo_right_url?: string;
  away_football_logo_left_url?: string;
  home_basketball_logo_url?: string;
  away_basketball_logo_url?: string;
  home_team_commentator?: string;
  away_team_commentator?: string;
  home_team_school_name?: string;
  away_team_school_name?: string;
  home_team_catch_phrase?: string;
  away_team_catch_phrase?: string;
  home_team_theme_color?: string;
  away_team_theme_color?: string;
  home_audio_url?: string;
  away_audio_url?: string;
  home_radioTipoffTaskArn?: string;
  away_radioTipoffTaskArn?: string;
  radioTipoffTaskArn?: string;
  bot_id?: string;
  game_type: string;
  status: string;
  created_at: string;
}

export interface GameResponse {
  success: boolean;
  game?: Game;
  error?: string;
}

export interface GameListResponse {
  success: boolean;
  games: Game[];
  /** True total across all matching games, not just the current page. */
  count: number;
  /** Present only when page/page_size were requested. */
  page?: number;
  page_size?: number;
  total_pages?: number;
  error?: string;
}

export interface ListGamesParams {
  status?: string;
  date?: string;
  from_date?: string;
  game_type?: string;
  team_name?: string;
  search?: string;
  bucket?: 'upcoming' | 'today' | 'past';
  page?: number;
  page_size?: number;
  /** 'schedule': today first, then upcoming (soonest first), then past (most recent first). */
  sort?: 'schedule';
  /** Hide games whose Eastern calendar date is before today. */
  exclude_past?: boolean;
  /** AG Grid filter model ({ column: model }); applied server-side across all pages. */
  filter_model?: Record<string, unknown>;
}

export interface ScheduledGameBucketCountsResponse {
  success: boolean;
  upcoming: number;
  today: number;
  past: number;
  error?: string;
}

export interface DeleteGameResponse {
  success: boolean;
  deleted_game_id?: string;
  error?: string;
}

export interface GameImportTemplateResponse {
  success: boolean;
  filename: string;
  file_base64: string;
}

export interface GameImportResponse {
  /** True only when every row was created. On any validation error nothing is created. */
  success: boolean;
  total_rows: number;
  created_count: number;
  failed_count: number;
  created: { row: number; game_id: string; game_name: string }[];
  errors: { row: number; errors: string[] }[];
}

export interface SeasonProductEntitlementBlock {
  entitlement: string[];
}

/** productId -> { entitlement: [...] } as stored in DynamoDB */
export type SeasonProductMap = Record<string, SeasonProductEntitlementBlock>;

export interface SeasonSportAccess {
  enabled: boolean;
  access_start: string;
  access_end: string;
  products: SeasonProductMap;
}

export interface SeasonSports {
  football: SeasonSportAccess;
  basketball: SeasonSportAccess;
}

export interface SeasonCreate {
  season_name: string;
  season_tag?: string;
  season_status?: 'active' | 'inactive';
  all_sport_pass_enabled?: boolean;
  all_sport_access_start?: string;
  all_sport_access_end?: string;
  /** Map of product id -> { entitlement: [...] }. Empty object is filled server-side. */
  all_sport_products?: SeasonProductMap;
  sports: SeasonSports;
}

export interface SeasonUpdate {
  season_name?: string;
  season_tag?: string;
  season_status?: 'active' | 'inactive';
  all_sport_pass_enabled?: boolean;
  all_sport_access_start?: string;
  all_sport_access_end?: string;
  all_sport_products?: SeasonProductMap;
  sports?: SeasonSports;
}

export interface SeasonRecord {
  season_tag: string;
  season_name: string;
  season_status: 'active' | 'inactive';
  all_sport_pass_enabled: boolean;
  all_sport_access_start: string;
  all_sport_access_end: string;
  all_sport_products: SeasonProductMap;
  sports: SeasonSports;
  created_at: string;
  updated_at: string;
}

export interface SeasonResponse {
  success: boolean;
  season?: SeasonRecord;
  error?: string;
}

export interface SeasonListResponse {
  success: boolean;
  seasons: SeasonRecord[];
  count: number;
  error?: string;
}

export interface DeleteSeasonResponse {
  success: boolean;
  deleted_season_tag?: string;
  error?: string;
}

// ============================================
// FIREBASE ANALYTICS TYPES
// ============================================

export interface FirebaseUserDailyAudioRow {
  date: string;
  user_id?: string;
  active_users?: number;
  audio_play_count?: number;
  audio_played?: number;
  audio_stop_count?: number;
  audio_stopped?: number;
  play_duration_seconds?: number;
  total_events?: number;
  is_live?: boolean;
  [key: string]: any;
}

export interface FirebaseUserDailyAudioResponse {
  success: boolean;
  message: string;
  start_date: string;
  end_date: string;
  user_id?: string;
  rows: FirebaseUserDailyAudioRow[];
  total_rows: number;
}

export interface BigQueryAnalyticsResponse<T = unknown> {
  success: boolean;
  rows: T[];
  total_rows: number;
  page?: number;
  page_size?: number;
  has_next_page?: boolean;
  has_previous_page?: boolean;
  error?: string;
}

// Firebase Analytics Event Types
export interface EventParam {
  key: string;
  value: {
    string_value: string | null;
    int_value: number | null;
    float_value: number | null;
    double_value: number | null;
  };
}

export interface AudioEvent {
  event_timestamp: number;
  event_name: string;
  user_id: string;
  game_id: string;
  event_params: EventParam[];
  latency_seconds?: number;
  is_live?: boolean;
  device_model?: string;
  app_version?: string;
  os_version?: string;
  reason?: string;
  position_seconds?: number;
}

export interface ListeningDurationEvent {
  event_timestamp: number;
  event_name: string;
  user_id: string;
  game_id: string;
  event_params: EventParam[];
}

export interface UserGameEventDetail {
  success: boolean;
  date: string;
  user_id: string;
  game_id: string;
  audio_play_events: AudioEvent[];
  audio_stop_events: AudioEvent[];
  listening_duration_events: ListeningDurationEvent[];
  total_events: number;
}

export interface StatsRow {
  total_play_events?: number;
  total_stop_events?: number;
  total_events?: number;
  active_users?: number;
  [key: string]: any;
}

export interface CurrentlyListeningUserRow {
  user_id: string;
  game_id?: string | null;
  game_name?: string | null;
  ga_session_id?: number | null;
  event_time_ms?: number | null;
  event_timestamp?: number | null;
  prior_duration_seconds?: number | null;
  status?: string | null;
  app_type?: string | null;
  device_type?: string | null;
  reason?: string | null;
  /** Purchase marker on the RTDB session node: converted_single_game, converted_football_season, ... */
  event?: string | null;
  is_free_trial?: boolean;
  email?: string | null;
  source?: string | null;
}

/** One user's row in /analytics/bq/free-trial-logs (GA `free_trial` / `converted_user` events,
 *  aggregated per user for one Eastern-time day). */
export interface FreeTrialLogRow {
  user: string;
  games: string[];
  /** First of `games`, kept for callers that expect one game. */
  game_name?: string | null;
  device?: string | null;
  /** Latest conversion event (converted_user rows only). */
  conversion_event?: string | null;
  conversion_events?: string[];
  last_reason?: string | null;
  first_seen?: string | null;
  last_seen?: string | null;
  events: number;
}

export interface FreeTrialLogsResponse {
  success: boolean;
  date?: string;
  free_trial: FreeTrialLogRow[];
  converted: FreeTrialLogRow[];
  error?: string;
}


/** One user (unique per list) from GET /firebase/analytics/bq/free-trial-logs (GA free_trial / converted_user). */
export interface FreeTrialLogRow {
  user: string;
  /** Every game the user was on that day. */
  games: string[];
  /** First of `games` (kept for single-game consumers). */
  game_name?: string | null;
  device?: string | null;
  /** Latest conversion: converted_single_game / converted_football_season / converted_basketball_season / converted_all_sport (converted rows only). */
  conversion_event?: string | null;
  /** All distinct conversion types the user had that day. */
  conversion_events?: string[];
  last_reason?: string | null;
  first_seen?: string | null; // ISO UTC
  last_seen?: string | null; // ISO UTC
  events: number;
}

export interface FreeTrialLogsResponse {
  success: boolean;
  date?: string;
  free_trial: FreeTrialLogRow[];
  converted: FreeTrialLogRow[];
  error?: string;
}

export interface DailyDetailsRow {
  date: string;
  user_id?: string;
  event_name?: string;
  event_count?: number;
  [key: string]: any;
}

// ============================================
// VENUE -> BOT ID MAPPING TYPES / API
// ============================================

export interface VenueBotMapping {
  venue: string;
  bot_id: string;
  created_at: string;
  updated_at: string;
}

export interface VenueBotMappingCreate {
  venue: string;
  bot_id: string;
}

export interface VenueBotMappingUpdate {
  bot_id: string;
}

export interface VenueBotMappingResponse {
  success: boolean;
  venue?: VenueBotMapping;
  error?: string;
}

export interface VenueBotMappingListResponse {
  success: boolean;
  venues: VenueBotMapping[];
  count: number;
  error?: string;
}

export interface DeleteVenueBotMappingResponse {
  success: boolean;
  deleted_venue?: string;
  error?: string;
}

export const venueBotMappingApi = {
  /**
   * POST /api/v1/venue-bot-mappings
   * Create a new venue -> bot ID mapping
   */
  async createVenueBotMapping(params: VenueBotMappingCreate): Promise<ApiResponse<VenueBotMappingResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/venue-bot-mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  /**
   * GET /api/v1/venue-bot-mappings
   * List all venue -> bot ID mappings
   */
  async listVenueBotMappings(): Promise<ApiResponse<VenueBotMappingListResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/venue-bot-mappings`);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/venue-bot-mappings/{venue}/{bot_id}
   * Get a specific venue mapping. Both venue and bot_id are required - together they're
   * the table's key (venue is the partition key, bot_id is the sort key).
   */
  async getVenueBotMapping(venue: string, botId: string): Promise<ApiResponse<VenueBotMappingResponse>> {
    const response = await fetch(
      `${SCHEDULER_API_BASE_URL}/venue-bot-mappings/${encodeURIComponent(venue)}/${encodeURIComponent(botId)}`
    );
    return handleResponse(response);
  },

  /**
   * PUT /api/v1/venue-bot-mappings/{venue}/{bot_id}
   * Rename a mapping's bot ID. `botId` identifies the existing row; `params.bot_id` is the
   * new value to rename it to.
   */
  async updateVenueBotMapping(
    venue: string,
    botId: string,
    params: VenueBotMappingUpdate
  ): Promise<ApiResponse<VenueBotMappingResponse>> {
    const response = await fetch(
      `${SCHEDULER_API_BASE_URL}/venue-bot-mappings/${encodeURIComponent(venue)}/${encodeURIComponent(botId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      }
    );
    return handleResponse(response);
  },

  /**
   * DELETE /api/v1/venue-bot-mappings/{venue}/{bot_id}
   * Delete a specific venue mapping.
   */
  async deleteVenueBotMapping(venue: string, botId: string): Promise<ApiResponse<DeleteVenueBotMappingResponse>> {
    const response = await fetch(
      `${SCHEDULER_API_BASE_URL}/venue-bot-mappings/${encodeURIComponent(venue)}/${encodeURIComponent(botId)}`,
      { method: 'DELETE' }
    );
    return handleResponse(response);
  },
};

// ============================================
// GAMES APIs
// ============================================

export const gamesApi = {
  /**
   * POST /api/v1/games
   * Create a new game
   */
  async createGame(params: GameCreate): Promise<ApiResponse<GameResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  /**
   * GET /api/v1/games
   * List games, optionally filtered/searched/bucketed/paginated. Omit page/page_size to
   * get every matching game in one response (unchanged from before pagination existed).
   */
  async listGames(params: ListGamesParams = {}): Promise<ApiResponse<GameListResponse>> {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.date) query.set('date', params.date);
    if (params.from_date) query.set('from_date', params.from_date);
    if (params.game_type) query.set('game_type', params.game_type);
    if (params.team_name) query.set('team_name', params.team_name);
    if (params.search) query.set('search', params.search);
    if (params.bucket) query.set('bucket', params.bucket);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.page_size !== undefined) query.set('page_size', String(params.page_size));
    if (params.sort) query.set('sort', params.sort);
    if (params.exclude_past) query.set('exclude_past', 'true');
    if (params.filter_model && Object.keys(params.filter_model).length > 0) {
      query.set('filter_model', JSON.stringify(params.filter_model));
    }

    const queryString = query.toString();
    const url = queryString
      ? `${SCHEDULER_API_BASE_URL}/games?${queryString}`
      : `${SCHEDULER_API_BASE_URL}/games`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/games/scheduled/bucket-counts
   * True counts of scheduled games per Eastern-time bucket (upcoming/today/past),
   * independent of pagination - drives the "Upcoming (N)" style tab labels.
   */
  async getScheduledGameBucketCounts(
    params: { game_type?: string; team_name?: string } = {}
  ): Promise<ApiResponse<ScheduledGameBucketCountsResponse>> {
    const query = new URLSearchParams();
    if (params.game_type) query.set('game_type', params.game_type);
    if (params.team_name) query.set('team_name', params.team_name);

    const queryString = query.toString();
    const url = queryString
      ? `${SCHEDULER_API_BASE_URL}/games/scheduled/bucket-counts?${queryString}`
      : `${SCHEDULER_API_BASE_URL}/games/scheduled/bucket-counts`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/games/scheduled/upcoming-and-today
   * Get all scheduled games for today and future dates (upcoming), sorted chronologically.
   * Single API endpoint for notification dropdowns.
   */
  async getScheduledTodayAndUpcomingGames(): Promise<ApiResponse<GameListResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/scheduled/upcoming-and-today`);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/games/team/{team_id}
   * List all games for a given team id
   */
  async listGamesByTeamId(teamId: string, status?: string): Promise<ApiResponse<GameListResponse>> {
    const url = status
      ? `${SCHEDULER_API_BASE_URL}/games/team/${encodeURIComponent(teamId)}?status=${encodeURIComponent(status)}`
      : `${SCHEDULER_API_BASE_URL}/games/team/${encodeURIComponent(teamId)}`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/games/{game_id}
   * Get a specific game
   */
  async getGame(gameId: string): Promise<ApiResponse<GameResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/${encodeURIComponent(gameId)}`);
    return handleResponse(response);
  },

  /**
   * PUT /api/v1/games/{game_id}
   * Update a game
   */
  async updateGame(gameId: string, params: GameUpdate): Promise<ApiResponse<GameResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/${encodeURIComponent(gameId)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  /**
   * PUT /api/v1/games/{game_id}/schedule
   * Update a scheduled game and refresh its orchestration schedule
   */
  async updateScheduledGame(gameId: string, params: GameUpdate): Promise<ApiResponse<GameResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/${encodeURIComponent(gameId)}/schedule`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  /**
   * POST /api/v1/games/{game_id}/publish
   * Publish a game by creating its orchestration schedule
   */
  async publishGame(gameId: string): Promise<ApiResponse<GameResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/${encodeURIComponent(gameId)}/publish`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  /**
   * POST /api/v1/games/{game_id}/revoke
   * Revoke a game and remove its orchestration schedule
   */
  async revokeGame(gameId: string): Promise<ApiResponse<GameResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/${encodeURIComponent(gameId)}/revoke`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  /**
   * DELETE /api/v1/games/{game_id}
   * Delete a game
   */
  async deleteGame(gameId: string): Promise<ApiResponse<DeleteGameResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/${encodeURIComponent(gameId)}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  /**
   * GET /api/v1/games/import/template
   * .xlsx import template (base64) with current Teams/Venues/Seasons reference sheets
   */
  async getImportTemplate(): Promise<ApiResponse<GameImportTemplateResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/import/template`);
    return handleResponse(response);
  },

  /**
   * POST /api/v1/games/import
   * Create games (unpublished) from a filled-in template
   */
  async importGames(file: File): Promise<ApiResponse<GameImportResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/games/import`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },
};

// ============================================
// TEAM DATA MANAGEMENT
// ============================================

export interface TeamRecord {
  team_id: string;
  name: string;
  abbreviation: string;
  game_types: ('football' | 'basketball')[];
  game_type: 'football' | 'basketball';
  status: 'active' | 'inactive';
  venue: string;
  basketball_venue: string;
  theme_color: string;
  text_color: string;
  main_logo_url: string;
  football_logo_right_url: string;
  football_logo_left_url: string;
  basketball_logo_url: string;
  audio_url: string;
  logo_url?: string;
  school_name: string;
  catch_phrase: string;
  commentator: string;
  basketball_commentator: string;
  created_at: string;
  updated_at: string;
}

export interface TeamUpsert {
  name: string;
  abbreviation: string;
  game_types: ('football' | 'basketball')[];
  status: 'active' | 'inactive';
  venue: string;
  basketball_venue: string;
  theme_color: string;
  text_color: string;
  main_logo_url: string;
  football_logo_right_url: string;
  football_logo_left_url: string;
  basketball_logo_url: string;
  audio_url: string;
  school_name: string;
  catch_phrase: string;
  commentator: string;
  basketball_commentator: string;
}

export interface TeamResponse {
  success: boolean;
  team?: TeamRecord;
  error?: string;
}

export interface TeamListResponse {
  success: boolean;
  teams: TeamRecord[];
  count: number;
  error?: string;
}

export interface TeamListFilters {
  game_type?: 'football' | 'basketball';
  status?: 'active' | 'inactive';
}

export interface DeleteTeamResponse {
  success: boolean;
  deleted_team_id?: string;
  error?: string;
}

export interface TeamImageUploadResult {
  url: string;
  key?: string;
}

const TEAM_LOGO_PRESIGN_URL = process.env.REACT_APP_TEAM_LOGO_PRESIGN_URL || '';
const TEAM_LOGO_UPLOAD_URL = process.env.REACT_APP_TEAM_LOGO_UPLOAD_URL || '';
const TEAM_ADMIN_API_BASE_URL = SCHEDULER_API_BASE_URL;
const TEAM_ADMIN_API_ROOT = TEAM_ADMIN_API_BASE_URL.replace(/\/$/, '');

const TEAM_STORAGE_KEY = 'homestream_admin_teams';

function getCurrentIsoTime() {
  return new Date().toISOString();
}

function normalizeTeamRecord(raw: Record<string, any>): TeamRecord {
  const mainLogoUrl = String(raw.main_logo_url || raw.logo_url || '').trim();
  const footballLogoRightUrl = String(
    raw.football_logo_right_url || raw.thumbnail_logo_url || ''
  ).trim();
  const footballLogoLeftUrl = String(
    raw.football_logo_left_url  || raw.thumbnail_logo_url || ''
  ).trim();
  const basketballLogoUrl = String(raw.basketball_logo_url || raw.banner_logo_url || '').trim();
  const themeColor = String(raw.theme_color || '#0E2569').trim();
  const textColor = String(raw.text_color || '#FFFFFF').trim();
  const legacyGameType = String(raw.game_type || 'football').toLowerCase() === 'basketball' ? 'basketball' : 'football';
  const gameTypesRaw = Array.isArray(raw.game_types) ? raw.game_types : raw.game_type ? [raw.game_type] : [legacyGameType];
  const gameTypes = Array.from(
    new Set(
      gameTypesRaw
        .map((gameType) => String(gameType).toLowerCase())
        .filter((gameType) => gameType === 'football' || gameType === 'basketball')
    )
  ) as ('football' | 'basketball')[];

  return {
    team_id: String(raw.team_id || ''),
    name: String(raw.name || ''),
    abbreviation: String(raw.abbreviation || ''),
    game_types: gameTypes,
    game_type: gameTypes[0] || legacyGameType,
    status: String(raw.status || 'active').toLowerCase() === 'inactive' ? 'inactive' : 'active',
    venue: String(raw.venue || ''),
    basketball_venue: String(raw.basketball_venue || ''),
    theme_color: themeColor,
    text_color: textColor,
    main_logo_url: mainLogoUrl,
    football_logo_right_url: footballLogoRightUrl,
    football_logo_left_url: footballLogoLeftUrl,
    basketball_logo_url: basketballLogoUrl,
    audio_url: String(raw.audio_url || '').trim(),
    logo_url: mainLogoUrl,
    school_name: String(raw.school_name || ''),
    catch_phrase: String(raw.catch_phrase || ''),
    commentator: String(raw.commentator || ''),
    basketball_commentator: String(raw.basketball_commentator || ''),
    created_at: String(raw.created_at || getCurrentIsoTime()),
    updated_at: String(raw.updated_at || getCurrentIsoTime()),
  };
}

function getTeamStorage(): TeamRecord[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(TEAM_STORAGE_KEY);
    if (!raw) {
      window.localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify([]));
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map((item) => normalizeTeamRecord(item)) as TeamRecord[];
  } catch {
    return [];
  }
}

function saveTeamStorage(teams: TeamRecord[]) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(teams));
}

function buildTeamRecord(id: string, existing: TeamRecord | null, params: TeamUpsert): TeamRecord {
  const now = getCurrentIsoTime();
  const themeColor = params.theme_color.trim();
  const textColor = params.text_color.trim();
  const gameTypes = Array.from(
    new Set(
      params.game_types
        .map((gameType) => gameType.toLowerCase())
        .filter((gameType) => gameType === 'football' || gameType === 'basketball')
    )
  ) as ('football' | 'basketball')[];

  return {
    team_id: id,
    name: params.name.trim(),
    abbreviation: params.abbreviation.trim().toUpperCase(),
    game_types: gameTypes,
    game_type: gameTypes[0] || 'football',
    status: params.status,
    venue: params.venue.trim(),
    basketball_venue: params.basketball_venue.trim(),
    theme_color: themeColor,
    text_color: textColor,
    main_logo_url: params.main_logo_url.trim(),
    football_logo_right_url: params.football_logo_right_url.trim(),
    football_logo_left_url: params.football_logo_left_url.trim(),
    basketball_logo_url: params.basketball_logo_url.trim(),
    audio_url: params.audio_url.trim(),
    logo_url: params.main_logo_url.trim(),
    school_name: params.school_name.trim(),
    catch_phrase: params.catch_phrase.trim(),
    commentator: params.commentator.trim(),
    basketball_commentator: params.basketball_commentator.trim(),
    created_at: existing?.created_at ?? now,
    updated_at: now,
  };
}

export const teamApi = {
  async listTeams(filters?: TeamListFilters): Promise<ApiResponse<TeamListResponse>> {
    if (TEAM_ADMIN_API_ROOT) {
      const params = new URLSearchParams();
      if (filters?.game_type) params.append('game_type', filters.game_type);
      if (filters?.status) params.append('status', filters.status);
      const url = params.toString()
        ? `${TEAM_ADMIN_API_ROOT}/teams?${params.toString()}`
        : `${TEAM_ADMIN_API_ROOT}/teams`;
      const response = await fetch(url);
      return handleResponse(response);
    }

    const teams = getTeamStorage().filter((team) => {
      const matchesGameType =
        !filters?.game_type ||
        team.game_types.includes(filters.game_type) ||
        team.game_type === filters.game_type;
      const matchesStatus = !filters?.status || team.status === filters.status;
      return matchesGameType && matchesStatus;
    });
    return {
      success: true,
      data: {
        success: true,
        teams,
        count: teams.length,
      },
    };
  },

  async getTeam(teamId: string): Promise<ApiResponse<TeamResponse>> {
    if (TEAM_ADMIN_API_ROOT) {
      const response = await fetch(`${TEAM_ADMIN_API_ROOT}/teams/${encodeURIComponent(teamId)}`);
      return handleResponse(response);
    }

    const teams = getTeamStorage();
    const team = teams.find((item) => item.team_id === teamId);

    if (!team) {
      return {
        success: false,
        error: 'Team not found',
      };
    }

    return {
      success: true,
      data: {
        success: true,
        team,
      },
    };
  },

  async createTeam(params: TeamUpsert): Promise<ApiResponse<TeamResponse>> {
    if (TEAM_ADMIN_API_ROOT) {
      const response = await fetch(`${TEAM_ADMIN_API_ROOT}/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      return handleResponse(response);
    }

    const teams = getTeamStorage();
    const team_id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `team_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const record = buildTeamRecord(team_id, null, params);

    teams.unshift(record);
    saveTeamStorage(teams);

    return {
      success: true,
      data: {
        success: true,
        team: record,
      },
    };
  },

  async updateTeam(teamId: string, params: TeamUpsert): Promise<ApiResponse<TeamResponse>> {
    if (TEAM_ADMIN_API_ROOT) {
      const response = await fetch(`${TEAM_ADMIN_API_ROOT}/teams/${encodeURIComponent(teamId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      return handleResponse(response);
    }

    const teams = getTeamStorage();
    const index = teams.findIndex((item) => item.team_id === teamId);

    if (index === -1) {
      return {
        success: false,
        error: 'Team not found',
      };
    }

    const updated = buildTeamRecord(teamId, teams[index], params);
    teams[index] = updated;
    saveTeamStorage(teams);

    return {
      success: true,
      data: {
        success: true,
        team: updated,
      },
    };
  },

  async deleteTeam(teamId: string): Promise<ApiResponse<DeleteTeamResponse>> {
    if (TEAM_ADMIN_API_ROOT) {
      const response = await fetch(`${TEAM_ADMIN_API_ROOT}/teams/${encodeURIComponent(teamId)}`, {
        method: 'DELETE',
      });
      return handleResponse(response);
    }

    const teams = getTeamStorage();
    const nextTeams = teams.filter((item) => item.team_id !== teamId);

    if (nextTeams.length === teams.length) {
      return {
        success: false,
        error: 'Team not found',
      };
    }

    saveTeamStorage(nextTeams);

    return {
      success: true,
      data: {
        success: true,
        deleted_team_id: teamId,
      },
    };
  },

  async uploadTeamImage(file: File, imageType: 'main' | 'thumbnail' | 'banner' = 'main'): Promise<ApiResponse<TeamImageUploadResult>> {
    if (!file) {
      return { success: false, error: 'No file selected' };
    }

    try {
      if (TEAM_ADMIN_API_ROOT) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('image_type', imageType);

        const response = await fetch(`${TEAM_ADMIN_API_ROOT}/teams/upload-image`, {
          method: 'POST',
          body: formData,
        });

        return handleResponse(response);
      }

      if (TEAM_LOGO_PRESIGN_URL) {
        const presignResponse = await fetch(TEAM_LOGO_PRESIGN_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            file_name: file.name,
            file_type: file.type || 'application/octet-stream',
          }),
        });

        if (!presignResponse.ok) {
          const errorData = await presignResponse.json().catch(() => ({}));
          return {
            success: false,
            error: errorData.detail || errorData.message || `HTTP ${presignResponse.status}: ${presignResponse.statusText}`,
          };
        }

        const presignData = await presignResponse.json();
        const uploadUrl = presignData.upload_url || presignData.uploadUrl;
        const publicUrl = presignData.public_url || presignData.publicUrl || presignData.url;
        const key = presignData.key;

        if (!uploadUrl || !publicUrl) {
          return {
            success: false,
            error: 'Upload service did not return a valid S3 upload URL.',
          };
        }

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          return {
            success: false,
            error: `Failed to upload image to S3 (HTTP ${uploadResponse.status})`,
          };
        }

        return {
          success: true,
          data: {
            url: publicUrl,
            key,
          },
        };
      }

      if (TEAM_LOGO_UPLOAD_URL) {
        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await fetch(TEAM_LOGO_UPLOAD_URL, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          return {
            success: false,
            error: errorData.detail || errorData.message || `HTTP ${uploadResponse.status}: ${uploadResponse.statusText}`,
          };
        }

        const uploadData = await uploadResponse.json();
        const url = uploadData.url || uploadData.public_url || uploadData.location;

        if (!url) {
          return {
            success: false,
            error: 'Upload service did not return an image URL.',
          };
        }

        return {
          success: true,
          data: {
            url,
            key: uploadData.key,
          },
        };
      }

      return {
        success: false,
        error:
          'Team image upload is not configured. Set REACT_APP_TEAM_LOGO_PRESIGN_URL or REACT_APP_TEAM_LOGO_UPLOAD_URL.',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown upload error',
      };
    }
  },

  async uploadTeamLogo(file: File, imageType: 'main' | 'thumbnail' | 'banner' = 'main'): Promise<ApiResponse<TeamImageUploadResult>> {
    return this.uploadTeamImage(file, imageType);
  },
};

export const adminTeamApi = {
  listTeams(filters?: TeamListFilters): Promise<ApiResponse<TeamListResponse>> {
    return teamApi.listTeams(filters);
  },

  getTeam(teamId: string): Promise<ApiResponse<TeamResponse>> {
    return teamApi.getTeam(teamId);
  },

  createTeam(params: TeamUpsert): Promise<ApiResponse<TeamResponse>> {
    return teamApi.createTeam(params);
  },

  updateTeam(teamId: string, params: TeamUpsert): Promise<ApiResponse<TeamResponse>> {
    return teamApi.updateTeam(teamId, params);
  },

  deleteTeam(teamId: string): Promise<ApiResponse<DeleteTeamResponse>> {
    return teamApi.deleteTeam(teamId);
  },

  uploadTeamImage(file: File): Promise<ApiResponse<TeamImageUploadResult>> {
    return teamApi.uploadTeamImage(file);
  },
};

// ============================================
// SEASON RECORD APIs
// ============================================

export const seasonApi = {
  /**
   * POST /api/v1/seasons
   * Create a new season record
   */
  async createSeason(params: SeasonCreate): Promise<ApiResponse<SeasonResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/seasons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  /**
   * GET /api/v1/seasons
   * List all season records
   */
  async listSeasons(status?: string): Promise<ApiResponse<SeasonListResponse>> {
    const url = status
      ? `${SCHEDULER_API_BASE_URL}/seasons?status=${encodeURIComponent(status)}`
      : `${SCHEDULER_API_BASE_URL}/seasons`;
    const response = await fetch(url);
    return handleResponse(response);
  },

  /**
   * GET /api/v1/seasons/{season_tag}
   * Get a specific season record
   */
  async getSeason(seasonTag: string): Promise<ApiResponse<SeasonResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/seasons/${encodeURIComponent(seasonTag)}`);
    return handleResponse(response);
  },

  /**
   * PUT /api/v1/seasons/{season_tag}
   * Update a season record
   */
  async updateSeason(seasonTag: string, params: SeasonUpdate): Promise<ApiResponse<SeasonResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/seasons/${encodeURIComponent(seasonTag)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  /**
   * DELETE /api/v1/seasons/{season_tag}
   * Delete a season record
   */
  async deleteSeason(seasonTag: string): Promise<ApiResponse<DeleteSeasonResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/seasons/${encodeURIComponent(seasonTag)}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// ============================================
// FIREBASE ANALYTICS APIs - BigQuery endpoints
// ============================================

/** Install attribution from preferences.marketing_attribution (AppsFlyer), normalised by the backend. */
export interface UserAttribution {
  type: 'organic' | 'non_organic';
  /** Social platform derived from af_channel / media_source; null when not one we recognise. */
  platform: 'instagram' | 'facebook' | 'x' | null;
  af_status?: string;
  media_source?: string;
  af_channel?: string;
  campaign?: string;
  af_adset?: string;
  af_ad?: string;
  af_sub1?: string;
  deep_link_value?: string;
  match_type?: string;
  is_retargeting?: string;
  click_time?: string;
  install_time?: string;
  appsflyer_id?: string;
}

export interface UserAttributionResponse {
  success: boolean;
  table_name: string;
  /** Keyed by the ids sent (email, anonymous uuid or Clerk user id); null = no attribution. */
  attributions: Record<string, UserAttribution | null>;
  /** preferences.location as saved by the app (e.g. "<area>, <city>, <state>, <zip>"); null = none. */
  locations?: Record<string, string | null>;
}

export const firebaseAnalyticsApi = {
  /** POST /api/v1/firebase/analytics/attribution - organic / non-organic and location per user. */
  async getAttributions(ids: string[], app?: string): Promise<ApiResponse<UserAttributionResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/attribution`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, app }),
    });
    return handleResponse(response);
  },

  /**
   * GET /api/firebase/analytics/bq/daily-summary
   * Get aggregated daily audio activity summary
   */
  async getDailySummary(params: {
    startDate: string;
    endDate: string;
    app?: string;
    limit?: number;
    offset?: number;
  }): Promise<BigQueryAnalyticsResponse<FirebaseUserDailyAudioRow>> {
    const query = new URLSearchParams();
    query.append('start_date', params.startDate);
    query.append('end_date', params.endDate);
    if (params.app) {
      query.append('app', params.app);
    }
    if (params.limit !== undefined) {
      query.append('limit', String(params.limit));
    }
    if (params.offset !== undefined) {
      query.append('offset', String(params.offset));
    }

    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/daily-details?${query.toString()}`);
      if (response.status !== 200) {
        return { success: false, rows: [], total_rows: 0 };
      }
      const responseData = await response.json();
      // Backend returns { success: true, data: { rows, total_rows } }
      if (responseData.success ) {
        return {
          success: true,
          rows: responseData.rows || [],
          total_rows: responseData.total_rows || 0,
        };
      }
      return { success: false, rows: [], total_rows: 0 };
    } catch (error) {
      console.error('Error fetching daily summary:', error);
      return { success: false, rows: [], total_rows: 0 };
    }
  },

  /**
   * GET /api/firebase/analytics/bq/daily-details
   * Get detailed event data for Event Data By Date table
   */
  async getDailyDetails(params: {
    startDate: string;
    endDate: string;
    app?: string;
    limit?: number;
    offset?: number;
    page?: number;
    userId?: string;
    gameName?: string;
  }): Promise<BigQueryAnalyticsResponse<DailyDetailsRow>> {
    const query = new URLSearchParams();
    query.append('start_date', params.startDate);
    query.append('end_date', params.endDate);
    if (params.app) {
      query.append('app', params.app);
    }
    if (params.limit !== undefined) {
      query.append('limit', String(params.limit));
    }
    if (params.offset !== undefined) {
      query.append('offset', String(params.offset));
    }
    if (params.page !== undefined) {
      query.append('page', String(params.page));
    }
    if (params.userId) {
      query.append('user_id', params.userId);
    }
    if (params.gameName) {
      query.append('game_name', params.gameName);
    }

    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/daily-details?${query.toString()}`);
      if (!response.ok) {
        return { success: false, rows: [], total_rows: 0 };
      }
      const responseData = await response.json();
      // Backend returns { success: true, rows, total_rows, page metadata }
      if (responseData.success) {
        return {
          success: true,
          rows: responseData.rows || [],
          total_rows: responseData.total_rows || 0,
          page: responseData.page,
          page_size: responseData.page_size,
          has_next_page: responseData.has_next_page,
          has_previous_page: responseData.has_previous_page,
        };
      }
      return { success: false, rows: [], total_rows: 0 };
    } catch (error) {
      console.error('Error fetching daily details:', error);
      return { success: false, rows: [], total_rows: 0 };
    }
  },

  /**
   * GET /api/firebase/analytics/bq/top-events
   * Get top events by count
   */
  async getTopEvents(params: {
    startDate: string;
    endDate: string;
    limit?: number;
    app?: string;
  }): Promise<BigQueryAnalyticsResponse> {
    const query = new URLSearchParams();
    query.append('start_date', params.startDate);
    query.append('end_date', params.endDate);
    if (params.limit) {
      query.append('limit', String(params.limit));
    }
    if (params.app) {
      query.append('app', params.app);
    }

    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/top-events?${query.toString()}`);
      if (!response.ok) {
        return { success: false, rows: [], total_rows: 0 };
      }
      const responseData = await response.json();
      // Backend returns { success: true, data: { rows, total_rows } }
      if (responseData.success && responseData.data) {
        return {
          success: true,
          rows: responseData.data.rows || [],
          total_rows: responseData.data.total_rows || 0,
        };
      }
      return { success: false, rows: [], total_rows: 0 };
    } catch (error) {
      console.error('Error fetching top events:', error);
      return { success: false, rows: [], total_rows: 0 };
    }
  },

  /**
   * GET /api/firebase/analytics/bq/stats
   * Get overall statistics for dashboard cards
   */
  async getStats(params: {
    startDate: string;
    endDate: string;
    app?: string;
    liveWindowMinutes?: number;
    userId?: string;
    gameName?: string;
  }): Promise<BigQueryAnalyticsResponse<StatsRow>> {
    

    const query = new URLSearchParams();
    query.append('start_date', params.startDate);
    query.append('end_date', params.endDate);
    if (params.app) {
      query.append('app', params.app);
    }
    if (params.liveWindowMinutes !== undefined) {
      query.append('live_window_minutes', String(params.liveWindowMinutes));
    }
    if (params.userId) {
      query.append('user_id', params.userId);
    }
    if (params.gameName) {
      query.append('game_name', params.gameName);
    }

    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/stats?${query.toString()}`);
      if (!response.ok) {
        return { success: false, rows: [], total_rows: 0 };
      }
      const responseData = await response.json();
      // Backend returns { success: true, data: { total_play_events, total_stop_events, total_events, active_users, ... } }
      if (responseData.success && responseData.data) {
        return {
          success: true,
          rows: [responseData.data as StatsRow],
          total_rows: 1,
        };
      }
      return { success: false, rows: [], total_rows: 0 };
    } catch (error) {
      console.error('Error fetching stats:', error);
      return { success: false, rows: [], total_rows: 0 };
    }
  },

  /**
   * GET /api/firebase/analytics/bq/currently-listening
   * Get users whose latest event is audio_play without a stop
   */
  async getCurrentlyListeningUsers(app?: string): Promise<BigQueryAnalyticsResponse<CurrentlyListeningUserRow>> {
    const query = new URLSearchParams();
    if (app) {
      query.append('app', app);
    }

    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/currently-listening?${query.toString()}`);
      if (!response.ok) {
        return { success: false, rows: [], total_rows: 0 };
      }
      const responseData = await response.json();
      if (responseData.success) {
        return {
          success: true,
          rows: responseData.rows || [],
          total_rows: responseData.total_rows || 0,
        };
      }
      return { success: false, rows: [], total_rows: 0 };
    } catch (error) {
      console.error('Error fetching currently listening users:', error);
      return { success: false, rows: [], total_rows: 0 };
    }
  },

  /**
   * GET /api/firebase/analytics/bq/free-trial-logs
   * Past free-trial and converted users for one Eastern-time day (BigQuery / GA events).
   */
  async getFreeTrialLogs(date: string, app?: string): Promise<FreeTrialLogsResponse> {
    const query = new URLSearchParams({ date });
    if (app) query.append('app', app);
    const empty: FreeTrialLogsResponse = { success: false, free_trial: [], converted: [] };
    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/free-trial-logs?${query.toString()}`);
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        return { ...empty, error: data?.detail || data?.error || `Request failed (${response.status})` };
      }
      return { success: true, date: data.date, free_trial: data.free_trial || [], converted: data.converted || [] };
    } catch (error) {
      return { ...empty, error: error instanceof Error ? error.message : 'Failed to load free trial logs' };
    }
  },

  /**
   * GET /api/firebase/analytics/bq/dropped-listeners
   * Today's Realtime DB sessions that are no longer playing, with the app's reason.
   */
  async getDroppedListeners(app?: string): Promise<BigQueryAnalyticsResponse<CurrentlyListeningUserRow>> {
    const query = new URLSearchParams();
    if (app) {
      query.append('app', app);
    }

    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/dropped-listeners?${query.toString()}`);
      if (!response.ok) {
        return { success: false, rows: [], total_rows: 0 };
      }
      const responseData = await response.json();
      if (responseData.success) {
        return {
          success: true,
          rows: responseData.rows || [],
          total_rows: responseData.total_rows || 0,
        };
      }
      return { success: false, rows: [], total_rows: 0 };
    } catch (error) {
      console.error('Error fetching dropped listeners:', error);
      return { success: false, rows: [], total_rows: 0 };
    }
  },

  /**
   * GET /api/firebase/analytics/bq/user-activity
   * Get per-user activity metrics
   */
  async getUserActivity(params: {
    startDate: string;
    endDate: string;
    app?: string;
  }): Promise<BigQueryAnalyticsResponse> {
    const query = new URLSearchParams();
    query.append('start_date', params.startDate);
    query.append('end_date', params.endDate);
    if (params.app) {
      query.append('app', params.app);
    }

    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/user-activity?${query.toString()}`);
      if (!response.ok) {
        return { success: false, rows: [], total_rows: 0 };
      }
      const responseData = await response.json();
      // Backend returns { success: true, data: { rows, total_rows } }
      if (responseData.success && responseData.data) {
        return {
          success: true,
          rows: responseData.data.rows || [],
          total_rows: responseData.data.total_rows || 0,
        };
      }
      return { success: false, rows: [], total_rows: 0 };
    } catch (error) {
      console.error('Error fetching user activity:', error);
      return { success: false, rows: [], total_rows: 0 };
    }
  },

  /**
   * GET /api/firebase/analytics/bq/user-game-event-details
   * Get detailed user game event information for a specific date, user, and game
   */
  async getUserGameEventDetails(params: {
    date: string; // YYYY-MM-DD format
    userId: string;
    gameId: string;
    app?: string;
  }): Promise<UserGameEventDetail> {
    const query = new URLSearchParams();
    query.append('date', params.date);
    query.append('user_id', params.userId);
    query.append('game_id', params.gameId);
    if (params.app) {
      query.append('app', params.app);
    }

    try {
      const response = await fetch(`${SCHEDULER_API_BASE_URL}/firebase/analytics/bq/user-game-event-details?${query.toString()}`);
      if (!response.ok) {
        return {
          success: false,
          date: params.date,
          user_id: params.userId,
          game_id: params.gameId,
          audio_play_events: [],
          audio_stop_events: [],
          listening_duration_events: [],
          total_events: 0,
        };
      }
      const data = await response.json();
      return data as UserGameEventDetail;
    } catch (error) {
      console.error('Error fetching user game event details:', error);
      return {
        success: false,
        date: params.date,
        user_id: params.userId,
        game_id: params.gameId,
        audio_play_events: [],
        audio_stop_events: [],
        listening_duration_events: [],
        total_events: 0,
      };
    }
  },
};


// ============================================
// PRODUCTS TYPES / API
// ============================================

export interface Product {
  product_id: string;
  product_name: string;
  base_plan_id: string;
  entitlements: string[];
  game_type: string;
  is_active: boolean;
  offers: Record<string, string[]>;
  product_type: string;
  valid_from: string;
  valid_till: string;
}

export interface ProductOffersUpdate {
  offers: Record<string, string[]>;
}

export interface ProductResponse {
  success: boolean;
  product?: Product;
  error?: string;
}

export interface ProductListResponse {
  success: boolean;
  products: Product[];
  count: number;
  error?: string;
}

export const productApi = {
  /**
   * GET /api/v1/products
   * List all products
   */
  async listProducts(): Promise<ApiResponse<ProductListResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/products`);
    return handleResponse(response);
  },

  /**
   * PUT /api/v1/products/{product_id}/{product_name}/offers
   * Update only the offers map for a product. Both product_id and product_name are
   * required - together they're the table's key (product_id is the partition key,
   * product_name is the sort key).
   */
  async updateProductOffers(
    productId: string,
    productName: string,
    params: ProductOffersUpdate
  ): Promise<ApiResponse<ProductResponse>> {
    const response = await fetch(
      `${SCHEDULER_API_BASE_URL}/products/${encodeURIComponent(productId)}/${encodeURIComponent(productName)}/offers`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      }
    );
    return handleResponse(response);
  },
};

// ============================================
// APP SETTINGS (dev-settings / prod-settings table)
// ============================================

export interface PreviewUrlSetting {
  enabled: boolean;
  expires_in_minutes: number;
  sport: 'all' | 'basketball' | 'football';
  game_scope: 'all' | 'specific';
  game_id: string | null;
}

export interface PreviewUrlSettingResponse {
  success: boolean;
  setting: PreviewUrlSetting;
  /** False when nothing is saved yet and the backend returned defaults. */
  exists: boolean;
  updated_at?: string | null;
  table_name: string;
}

export interface InternalAccount {
  /** Email or app user id, lower-cased by the backend. */
  account: string;
  note?: string | null;
}

export interface InternalAccountsSetting {
  accounts: InternalAccount[];
}

export interface InternalAccountsSettingResponse {
  success: boolean;
  setting: InternalAccountsSetting;
  exists: boolean;
  updated_at?: string | null;
  table_name: string;
}

export interface UserTeamExportSummary {
  environment: string;
  generated_at: string;
  clerk_total_count: number | null;
  clerk_users_fetched: number;
  clerk_counts_match: boolean;
  preferences_table: string;
  preferences_scanned: number;
  preferences_with_favorite_team: number;
  preferences_without_clerk_user: number;
  teams_table: string;
  teams_scanned: number;
  matched: number;
  unmatched: number;
  unmatched_no_preference: number;
  unmatched_no_favorite_team: number;
  unmatched_unknown_team_id: number;
  duration_seconds: number;
}

export interface UserTeamExportResponse {
  success: boolean;
  summary: UserTeamExportSummary;
  filename: string;
  file_base64: string;
}

export const appSettingsApi = {
  /** GET /api/v1/settings/preview-url */
  async getPreviewUrlSetting(): Promise<ApiResponse<PreviewUrlSettingResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/settings/preview-url`);
    return handleResponse(response);
  },

  /** PUT /api/v1/settings/preview-url - stored under setting-key "preview-url-setting". */
  async savePreviewUrlSetting(setting: PreviewUrlSetting): Promise<ApiResponse<PreviewUrlSettingResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/settings/preview-url`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(setting),
    });
    return handleResponse(response);
  },

  /** GET /api/v1/settings/internal-accounts - accounts excluded from analytics. */
  async getInternalAccountsSetting(): Promise<ApiResponse<InternalAccountsSettingResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/settings/internal-accounts`);
    return handleResponse(response);
  },

  /** PUT /api/v1/settings/internal-accounts - stored under setting-key "internal-access-accounts". */
  async saveInternalAccountsSetting(setting: InternalAccountsSetting): Promise<ApiResponse<InternalAccountsSettingResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/settings/internal-accounts`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(setting),
    });
    return handleResponse(response);
  },

  /** POST /api/v1/settings/user-team-export - every Clerk user joined with their favorite team
   *  (user-preferences + teams tables) as a base64 .xlsx plus the counts. Can take ~a minute. */
  async buildUserTeamExport(): Promise<ApiResponse<UserTeamExportResponse>> {
    const response = await fetch(`${SCHEDULER_API_BASE_URL}/settings/user-team-export`, { method: 'POST' });
    return handleResponse(response);
  },
};

// ============================================
// MANUAL OPERATIONS NOTIFICATIONS TYPES / API
// ============================================
// Admin-triggered notifications sent over MQTT (AWS IoT Core) and persisted to
// DynamoDB. Distinct from the Firebase push notifications above - see
// routers/manual_notifications.py in HomeStream-Admin-Backend.

export type ManualNotificationType = 'game_stream' | 'team' | 'generic';
export type GameStreamStatus = 'resumed' | 'halted';
// Hex color codes: #FF3B30 (Red), #FF9500 (Amber), #34C759 (Green)
export type NotificationSeverity = '#FF3B30' | '#FF9500' | '#34C759' | 'red' | 'amber' | 'green';

export interface ManualNotificationCreateRequest {
  notification_type: ManualNotificationType;
  title: string;
  message: string;
  topic?: string;
  game_id?: string;
  game_name?: string;
  stream_status?: GameStreamStatus;
  team_id?: string;
  team_name?: string;
  severity: NotificationSeverity;
}

export interface ManualNotificationRecord {
  notification_id: string;
  notification_type: ManualNotificationType;
  title: string;
  message: string;
  topic: string;
  created_at: string;
  severity?: NotificationSeverity;
  game_id?: string;
  game_name?: string;
  stream_status?: GameStreamStatus;
  team_id?: string;
  team_name?: string;
}

export interface ManualNotificationResponse {
  success: boolean;
  notification?: ManualNotificationRecord;
  error?: string;
}

export interface ManualNotificationListResponse {
  success: boolean;
  notifications: ManualNotificationRecord[];
  count: number;
  error?: string;
}

export const manualNotificationsApi = {
  /**
   * POST /api/v1/manual-notifications
   * Send a manual notification - persisted to DynamoDB and published to AWS IoT Core.
   */
  async send(request: ManualNotificationCreateRequest): Promise<ApiResponse<ManualNotificationResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/manual-notifications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    return handleResponse(response);
  },

  /**
   * GET /api/v1/manual-notifications
   * List manual notifications, most recent first.
   */
  async list(notificationType?: ManualNotificationType): Promise<ApiResponse<ManualNotificationListResponse>> {
    const url = notificationType
      ? `${ADMIN_API_BASE_URL}/manual-notifications?notification_type=${encodeURIComponent(notificationType)}`
      : `${ADMIN_API_BASE_URL}/manual-notifications`;
    const response = await fetch(url);
    return handleResponse(response);
  },
};

// ============================================
// OPS: SYNC DETECTION TYPES / API
// ============================================
// Reads user/image sync-matching attempts logged by the mobile clients.
// See routers/ops_sync.py in HomeStream-Admin-Backend.

export interface OpsSyncUser {
  user_email: string;
  total_requests: number;
  failures: number;
  total_attempts?: number;
  successful_attempts?: number;
  failed_attempts?: number;
  failure_breakdown: Record<string, number>;
  last_attempt_result?: string;
  is_last_attempt_failed?: boolean;
}

export interface OpsSyncUsersResponse {
  success: boolean;
  users: OpsSyncUser[];
  unsynced_users?: OpsSyncUser[];
  time_range_minutes: number;
  error?: string;
}

export interface OpsSyncRequestDetail {
  id: string;
  created_at: string;
  user_id: string;
  game_name: string;
  game_type: string;
  game_time: string;
  period: string | number;
  success: boolean;
  time_difference_seconds: number;
  attempt_id: string;
  request_type: string;
  image_browser_url: string;
  image_object_path: string;
  processing_time_seconds: number;
  result: string;
  genius_matches: number;
  matched_frame_data: Record<string, unknown>;
  llm_fallback_attempted?: boolean;
  llm_fallback_used?: boolean;
  llm_fallback_trigger_error?: string;
  llm_fallback_original_game_time?: string;
  llm_fallback_original_period?: string;
  llm_fallback_game_time?: string;
  llm_fallback_period?: string;
}

export interface OpsSyncAttempt {
  attempt_id: string;
  game_time: string;
  period: string | number;
  game_id: string;
  game_type: string;
  request_count: number;
  success_count: number;
  failure_count: number;
  status: 'success' | 'partial' | 'failed';
  first_request: string;
  last_request: string;
  requests: OpsSyncRequestDetail[];
}

export interface OpsSyncAttemptsResponse {
  success: boolean;
  user_email: string;
  attempts: OpsSyncAttempt[];
  time_range_minutes: number;
  error?: string;
}

export interface OpsRefreshImageUrlResponse {
  success: boolean;
  image_browser_url: string;
  error?: string;
}

export interface OpsSyncGame {
  game_name: string;
  requests: number;
  users: number;
}

export interface OpsSyncGamesResponse {
  success: boolean;
  games: OpsSyncGame[];
  time_range_minutes: number;
  error?: string;
}

export interface OpsSyncImageFile {
  key: string;
  name: string;
  attempt_id?: string | null;
  size: number;
  last_modified: string;
  url: string;
}

export interface OpsSyncImageBrowseResponse {
  success: boolean;
  prefix: string;
  folders: string[];
  files: OpsSyncImageFile[];
  error?: string;
}

export const opsSyncApi = {
  /** GET /api/v1/ops/sync/users - active users with sync activity in the last N minutes. */
  async getUsers(minutes: number, gameName?: string): Promise<ApiResponse<OpsSyncUsersResponse>> {
    const params = new URLSearchParams({ minutes: String(minutes) });
    if (gameName) params.set('game_name', gameName);
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/sync/users?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/sync/attempts - a user's sync attempts, grouped by attempt_id. */
  async getAttempts(userEmail: string, minutes: number, gameName?: string): Promise<ApiResponse<OpsSyncAttemptsResponse>> {
    const params = new URLSearchParams({ user_email: userEmail, minutes: String(minutes) });
    if (gameName) params.set('game_name', gameName);
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/sync/attempts?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/sync/games - distinct game names with sync activity in the last N minutes. */
  async getGames(minutes: number): Promise<ApiResponse<OpsSyncGamesResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/sync/games?minutes=${minutes}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/sync/image-url - re-presign a screenshot's S3 key once the stored URL has expired. */
  async refreshImageUrl(objectPath: string): Promise<ApiResponse<OpsRefreshImageUrlResponse>> {
    const params = new URLSearchParams({ object_path: objectPath });
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/sync/image-url?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/sync/images - browse the sync-images S3 bucket one level at a time. */
  async browseImages(prefix: string = ''): Promise<ApiResponse<OpsSyncImageBrowseResponse>> {
    const params = new URLSearchParams({ prefix });
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/sync/images?${params.toString()}`);
    return handleResponse(response);
  },
};

// ============================================
// OPS: SERVICE HEALTH TYPES / API
// ============================================
// Game-orchestration Step Function executions and MQTT/audio/tipoff ECS task status.
// See routers/ops_health.py in HomeStream-Admin-Backend.

export interface OpsPhaseTimelineEntry {
  phase: string;
  started_at: string | null;
  status: 'completed' | 'in_progress' | 'failed' | 'pending';
}

export interface OpsOrchestration {
  execution_arn: string;
  name: string;
  status: 'RUNNING' | 'FAILED' | 'SUCCEEDED' | 'ABORTED';
  started_at: string;
  stopped_at: string;
  game_name: string;
  game_id: string;
  game_type: string;
  bot_id: string;
  venue: string;
  home_audio_url: string;
  away_audio_url: string;
  run_window_seconds: number;
  current_phase: string;
  phase_timeline: OpsPhaseTimelineEntry[];
  duration_seconds: number | null;
}

export interface OpsOrchestrationsResponse {
  success: boolean;
  orchestrations: OpsOrchestration[];
  error?: string;
}

export interface OpsEcsTask {
  task_arn: string;
  task_definition: string;
  family: string;
  status: string;
  desired_status: string;
  started_at: string;
  uptime_seconds: number | null;
  cpu: string;
  memory: string;
  stopped_reason: string;
  game_name: string;
  stream_direction: string;
  stream_url: string;
}

export interface OpsEcsTasksResponse {
  success: boolean;
  tasks: OpsEcsTask[];
  error?: string;
}

export interface OpsLiveScore {
  home_score: number;
  guest_score: number;
  clock: string;
  period: string | number;
  period_text: string;
  clock_status: string;
  source_utc: string;
  venue_name: string;
}

export interface OpsLiveScoreResponse {
  success: boolean;
  game_id: string;
  score: OpsLiveScore | null;
  error?: string;
}

export interface OpsTipoffDetail {
  created_at: string;
  source_utc: string;
  stream_url: string;
  confirmation_text: string;
  detection_type: string;
  is_manual_override: boolean;
  manual_edited_at: string;
  /** Audio records only: 'home' | 'away'. */
  audio_type?: string;
}

export interface OpsTipoffsResponse {
  success: boolean;
  game_id: string;
  tipoffs: { genius: OpsTipoffDetail | null; audio: OpsTipoffDetail | null };
  error?: string;
}

export interface OpsTipoffTaskLogEntry {
  timestamp: number;
  timestamp_iso: string;
  message: string;
}

export interface OpsTipoffTaskLogsResponse {
  success: boolean;
  game_name: string;
  direction: 'home' | 'away';
  task_id: string;
  log_group: string;
  log_stream: string;
  logs: OpsTipoffTaskLogEntry[];
  next_token?: string | null;
  error?: string;
}

export interface OpsGameDataLogEntry {
  source_utc: string;
  home_score: number;
  guest_score: number;
  clock: string;
  period: string | number;
  period_text: string;
  clock_status: string;
  play_clock: string;
  venue_name: string;
}

export interface OpsGameDataLogsResponse {
  success: boolean;
  game_name: string;
  game_type: string;
  logs: OpsGameDataLogEntry[];
  next_token?: string | null;
  error?: string;
}

export interface OpsMqttConnectionResponse {
  success: boolean;
  ws_url: string;
  endpoint: string;
  authorizer_name: string;
  token: string;
  error?: string;
}

// ============================================
// MOCK GENIUS TYPES / API
// ============================================
// Game orchestrator mock/test service (see MockGeniusPanel). No auth required.

export interface MockGeniusFilesResponse {
  audio_files: string[];
  data_files: string[];
}

export interface MockGeniusMilestone {
  label: string;
  state: 'pending' | 'active' | 'done' | 'error';
  time?: string;
}

export interface MockGeniusMilestonesResponse {
  game_name: string;
  status: string;
  milestones: MockGeniusMilestone[];
  logs: string;
}

export interface MockGeniusLookupGameResponse {
  found: boolean;
  game_id: string | null;
  game_name: string;
}

export interface MockGeniusStartGameRequest {
  env: string;
  game_type: string;
  game_name: string;
  game_id: string;
  input_file: string;
  home_audio_file: string;
  away_audio_file: string;
  run_window_seconds: number;
  audio_match_tipoff?: boolean;
  audio_match_lead_seconds?: number;
}

// Response shape beyond `success`/`error` (set by handleResponse) is not documented by the
// mock-genius service - callers should not assume additional fields are present.
export interface MockGeniusStartGameResponse {
  [key: string]: unknown;
}

export const mockGeniusApi = {
  /** GET /api/files - available game data (CSV/NDJSON) and audio (MP3) files. */
  async getFiles(): Promise<ApiResponse<MockGeniusFilesResponse>> {
    const response = await fetch(`${MOCK_GENIUS_API_BASE_URL}/api/files`);
    return handleResponse(response);
  },

  /** GET /api/lookup-game?game_name=... - resolve a game_name to its game_id. */
  async lookupGame(gameName: string): Promise<ApiResponse<MockGeniusLookupGameResponse>> {
    const params = new URLSearchParams({ game_name: gameName });
    const response = await fetch(`${MOCK_GENIUS_API_BASE_URL}/api/lookup-game?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/milestones - current orchestration run status, milestones, and logs. */
  async getMilestones(): Promise<ApiResponse<MockGeniusMilestonesResponse>> {
    const response = await fetch(`${MOCK_GENIUS_API_BASE_URL}/api/milestones`);
    return handleResponse(response);
  },

  /** POST /api/start - start a mock game run. Every field in the payload is required. */
  async startGame(params: MockGeniusStartGameRequest): Promise<ApiResponse<MockGeniusStartGameResponse>> {
    const response = await fetch(`${MOCK_GENIUS_API_BASE_URL}/api/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },
};

export const opsHealthApi = {
  /** GET /api/v1/ops/health/orchestrations - game orchestrations for a day (default: today). */
  async getOrchestrations(limit = 10, date?: string): Promise<ApiResponse<OpsOrchestrationsResponse>> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (date) params.set('date', date);
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/health/orchestrations?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/health/ecs-tasks - currently running MQTT/audio/tipoff ECS tasks. */
  async getEcsTasks(): Promise<ApiResponse<OpsEcsTasksResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/health/ecs-tasks`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/health/live-score - latest score for a game from the genius data table. */
  async getLiveScore(gameId: string, gameType: string): Promise<ApiResponse<OpsLiveScoreResponse>> {
    const params = new URLSearchParams({ game_id: gameId, game_type: gameType });
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/health/live-score?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/health/tipoffs - genius/audio tipoff detection timestamps for a game. */
  async getTipoffs(gameId: string, gameType: string, env: OpsEnv = 'prod', audioType: 'home' | 'away' = 'home'): Promise<ApiResponse<OpsTipoffsResponse>> {
    const params = new URLSearchParams({ game_id: gameId, game_type: gameType, env, audio_type: audioType });
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/health/tipoffs?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/health/tipoff-task-logs - CloudWatch logs for the running radio-tipoff task, newest first (UTC). Pass nextToken to page to older events. */
  async getTipoffTaskLogs(gameName: string, direction: 'home' | 'away' = 'home', limit = 200, nextToken?: string | null): Promise<ApiResponse<OpsTipoffTaskLogsResponse>> {
    const params = new URLSearchParams({ game_name: gameName, direction, limit: String(limit) });
    if (nextToken) params.set('next_token', nextToken);
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/health/tipoff-task-logs?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/health/game-data-logs - {env}-mqtt-{sport}-game-data records for a game, newest first. Pass nextToken to page to older records. */
  async getGameDataLogs(gameName: string, gameType: string, limit = 200, nextToken?: string | null): Promise<ApiResponse<OpsGameDataLogsResponse>> {
    const params = new URLSearchParams({ game_name: gameName, game_type: gameType, limit: String(limit) });
    if (nextToken) params.set('next_token', nextToken);
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/health/game-data-logs?${params.toString()}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/health/mqtt-connection - IoT token + wss:// URL for a live radio-tipoff MQTT subscription. */
  async getMqttConnection(): Promise<ApiResponse<OpsMqttConnectionResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/health/mqtt-connection`);
    return handleResponse(response);
  },
};

// ============================================
// OPS: INFRASTRUCTURE TYPES / API
// ============================================
// CloudWatch metrics for Lambda, ECS, DynamoDB, and CloudFront.
// See routers/ops_infrastructure.py in HomeStream-Admin-Backend.

export interface OpsLambdaMetric {
  function_name: string;
  invocations: number;
  errors: number;
  duration_p95_ms: number;
  throttles: number;
  error_rate: number;
}

export interface OpsEcsMetrics {
  cluster: string;
  cpu_percent: number;
  memory_percent: number;
  running_tasks: number;
}

export interface OpsDynamoDbMetric {
  table_name: string;
  reads: number;
  writes: number;
  throttles: number;
}

export interface OpsCloudFrontMetrics {
  distribution_id: string;
  requests: number;
  error_4xx_pct: number;
  error_5xx_pct: number;
  cache_hit_pct: number;
}

export interface OpsInfraMetricsResponse {
  success: boolean;
  minutes: number;
  lambda: OpsLambdaMetric[];
  ecs: OpsEcsMetrics | null;
  dynamodb: OpsDynamoDbMetric[];
  cloudfront: OpsCloudFrontMetrics | null;
  error?: string;
}

export const opsInfrastructureApi = {
  /** GET /api/v1/ops/infra/metrics - Lambda/ECS/DynamoDB/CloudFront metrics for a time window. */
  async getMetrics(minutes = 15): Promise<ApiResponse<OpsInfraMetricsResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/infra/metrics?minutes=${minutes}`);
    return handleResponse(response);
  },
};

// ============================================
// OPS: API HEALTH TYPES / API
// ============================================

export interface OpsApiHealthAttempt {
  ok: boolean;
  timestamp?: string | null;
  request_id?: string | null;
  error?: string | null;
}

export interface OpsApiHealthFunction {
  function_name: string;
  heading: string;
  attempts: OpsApiHealthAttempt[];
  error?: string | null;
  env?: 'dev' | 'prod' | null;
  status?: 'healthy' | 'unhealthy' | 'no_activity' | 'unknown' | null;
  invocations?: number | null;
  errors?: number | null;
  error_rate?: number | null;
  throttles?: number | null;
  duration_avg_ms?: number | null;
  duration_max_ms?: number | null;
  range_start?: string | null;
  range_end?: string | null;
}

export interface OpsApiHealthStatusResponse {
  success: boolean;
  hours: number;
  limit: number;
  range_start?: string;
  range_end?: string;
  functions: OpsApiHealthFunction[];
  error?: string;
}

export interface OpsApiHealthLogRow {
  function_name: string;
  heading?: string | null;
  timestamp?: string | null;
  message: string;
  request_id?: string | null;
}

export interface OpsApiHealthLogsResponse {
  success: boolean;
  hours: number;
  range_start?: string;
  range_end?: string;
  function_name?: string | null;
  logs: OpsApiHealthLogRow[];
  error?: string;
}

export const opsApiHealthApi = {
  /** GET /api/v1/ops/api-health/status - Lambda health from CloudWatch Metrics. */
  async getStatus(hours = 168, limit = 100): Promise<ApiResponse<OpsApiHealthStatusResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/api-health/status?hours=${hours}&limit=${limit}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/api-health/logs - raw log lines (all functions or one). */
  async getLogs(hours = 168, functionName?: string | null, limit = 200): Promise<ApiResponse<OpsApiHealthLogsResponse>> {
    const params = new URLSearchParams({ hours: String(hours), limit: String(limit) });
    if (functionName) params.set('function_name', functionName);
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/api-health/logs?${params.toString()}`);
    return handleResponse(response);
  },
};

// ============================================
// OPS: MANUAL OPERATION TYPES / API
// ============================================
// Launches MQTT/audio/tipoff ECS tasks, writes manual tipoff overrides, and manages
// single-game access grants. See routers/ops_manual.py in HomeStream-Admin-Backend.
// Distinct from manualNotificationsApi above, which sends MQTT push notifications.

export type OpsEnv = 'dev' | 'qa' | 'prod';

export interface OpsLaunchMqttRequest {
  env?: OpsEnv;
  game_name: string;
  game_type: string;
  bot_id: string;
  run_duration_hours?: string;
}

export interface OpsLaunchAudioRequest {
  env?: OpsEnv;
  game_name: string;
  stream_url: string;
  segment_duration?: string;
  playlist_size?: string;
}

export interface OpsLaunchTipoffRequest {
  env?: OpsEnv;
  game_name: string;
  game_type: string;
  stream_url: string;
  max_runtime_minutes?: string;
  detection_mode: 'kickoff_tipoff' | 'halftime_resumption';
  audio_type: 'home' | 'away';
}

export interface OpsUpdateTipoffRequest {
  env?: OpsEnv;
  game_id: string;
  game_type: string;
  tipoff_type: 'genius' | 'audio';
  timestamp: string;
  stream_url?: string;
  audio_type?: 'home' | 'away';
}

export interface OpsCheckAccessRequest {
  env?: OpsEnv;
  email: string;
  game_name?: string;
}

export interface OpsGrantAccessRequest {
  env?: OpsEnv;
  email: string;
  game_name: string;
}

export interface OpsLaunchTaskResponse {
  success: boolean;
  status: 'launched' | 'blocked' | 'failed';
  task_arn?: string;
  error?: string;
}

export interface OpsUpdateTipoffResponse {
  success: boolean;
  status: 'success' | 'error';
  detection_id?: string;
  error?: string;
}

export interface OpsPurchasedProduct {
  product: string;
  product_id: string;
  game_id: string;
  season_tag: string;
  purchase_mode: string;
  expiry?: number | null;
  subscription_status: string;
}

export interface OpsCheckAccessResponse {
  success: boolean;
  status: 'has_access' | 'no_access';
  email: string;
  user_id?: string | null;
  products: OpsPurchasedProduct[];
  error?: string;
}

export interface OpsGrantAccessResponse {
  success: boolean;
  status: 'granted' | 'already_has_access' | 'error';
  email: string;
  game_name?: string;
  game_id?: string;
  team_id?: string;
  product?: string;
  expires_at?: number;
  error?: string;
}

export interface OpsLookupGameResponse {
  success: boolean;
  found: boolean;
  game_id?: string;
  game_name?: string;
  error?: string;
}

export const opsManualApi = {
  /** POST /api/v1/ops/manual/launch-mqtt */
  async launchMqtt(request: OpsLaunchMqttRequest): Promise<ApiResponse<OpsLaunchTaskResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/manual/launch-mqtt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handleResponse(response);
  },

  /** POST /api/v1/ops/manual/launch-audio */
  async launchAudio(request: OpsLaunchAudioRequest): Promise<ApiResponse<OpsLaunchTaskResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/manual/launch-audio`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handleResponse(response);
  },

  /** POST /api/v1/ops/manual/launch-tipoff */
  async launchTipoff(request: OpsLaunchTipoffRequest): Promise<ApiResponse<OpsLaunchTaskResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/manual/launch-tipoff`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handleResponse(response);
  },

  /** POST /api/v1/ops/manual/update-tipoff */
  async updateTipoff(request: OpsUpdateTipoffRequest): Promise<ApiResponse<OpsUpdateTipoffResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/manual/update-tipoff`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handleResponse(response);
  },

  /** POST /api/v1/ops/manual/check-access */
  async checkAccess(request: OpsCheckAccessRequest): Promise<ApiResponse<OpsCheckAccessResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/manual/check-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handleResponse(response);
  },

  /** POST /api/v1/ops/manual/grant-access */
  async grantAccess(request: OpsGrantAccessRequest): Promise<ApiResponse<OpsGrantAccessResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/manual/grant-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return handleResponse(response);
  },

  /** GET /api/v1/ops/manual/lookup-game?game_name=... */
  async lookupGame(gameName: string): Promise<ApiResponse<OpsLookupGameResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/manual/lookup-game?game_name=${encodeURIComponent(gameName)}`);
    return handleResponse(response);
  },
};

// ============================================
// OPS: PURCHASERS BY SCHOOL
// ============================================

export interface PurchaserRow {
  email: string;
  user_id: string;
  product_id: string;
  product: string;
  product_label: string;
  purchase_mode: string;
  subscription_status: string;
  team_id: string;
  school_name: string;
  team_name: string;
  game_id: string;
  game_name: string;
  created_at: number;
  created_at_iso: string;
  is_test: boolean;
}

export interface PurchasersListResponse {
  success: boolean;
  purchasers: PurchaserRow[];
  count: number;
  error?: string;
}

export interface SchoolStat {
  team_id: string;
  school_name: string;
  total: number;
  by_product: Record<string, number>;
  by_status: Record<string, number>;
}

export interface PurchasersTotals {
  total: number;
  by_product: Record<string, number>;
  by_status: Record<string, number>;
}

export interface PurchasersStatsResponse {
  success: boolean;
  stats: SchoolStat[];
  totals: PurchasersTotals;
  error?: string;
}

export interface OpsPurchasersParams {
  team_id?: string;
  status?: string;
  include_test?: boolean;
  product_labels?: string[];
}

function buildPurchasersQuery(params: OpsPurchasersParams): string {
  const query = new URLSearchParams();
  if (params.team_id) query.set('team_id', params.team_id);
  if (params.status) query.set('status', params.status);
  if (params.include_test) query.set('include_test', 'true');
  (params.product_labels || []).forEach((label) => query.append('product', label));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

export const opsPurchasersApi = {
  /** GET /api/v1/ops/purchasers/list */
  async list(params: OpsPurchasersParams = {}): Promise<ApiResponse<PurchasersListResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/purchasers/list${buildPurchasersQuery(params)}`);
    return handleResponse(response);
  },

  /** GET /api/v1/ops/purchasers/stats */
  async stats(params: OpsPurchasersParams = {}): Promise<ApiResponse<PurchasersStatsResponse>> {
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/purchasers/stats${buildPurchasersQuery(params)}`);
    return handleResponse(response);
  },
};

export interface PurchaseTimelineEvent {
  kind: string;
  at: string;
  label: string;
  detail: string;
  source?: Record<string, unknown> | null;
}

export interface PurchaseJourney {
  journey_id: string;
  status: string;
  match_type: string;
  user_id: string;
  email: string;
  product_id: string;
  game_id: string;
  game_name: string;
  transaction_id: string;
  provider_or_store: string;
  price_label: string;
  latest_at: string;
  intent_count: number;
  events: PurchaseTimelineEvent[];
}

export interface PurchaseTimelineResponse {
  success: boolean;
  days: number;
  range_start: string;
  range_end: string;
  journeys: PurchaseJourney[];
  count: number;
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
  totals: Record<string, number>;
  error?: string;
}

export interface OpsPurchaseTimelineParams {
  from_date?: string;
  to_date?: string;
  days?: number;
  q?: string;
  status?: string;
  presence?: string;
  offset?: number;
  limit?: number;
}

export const opsPurchaseTimelineApi = {
  async list(params: OpsPurchaseTimelineParams = {}): Promise<ApiResponse<PurchaseTimelineResponse>> {
    const query = new URLSearchParams();
    if (params.from_date) query.set('from_date', params.from_date);
    if (params.to_date) query.set('to_date', params.to_date);
    if (params.days != null) query.set('days', String(params.days));
    if (params.q) query.set('q', params.q);
    if (params.status) query.set('status', params.status);
    if (params.presence) query.set('presence', params.presence);
    if (params.offset != null) query.set('offset', String(params.offset));
    if (params.limit != null) query.set('limit', String(params.limit));
    const qs = query.toString();
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/purchase-timeline${qs ? `?${qs}` : ''}`);
    return handleResponse(response);
  },
};

export interface PaywallCounterRow {
  user_id: string;
  email: string;
  paywall_listener: number;
  single_game: number;
  football: number;
  basketball: number;
  all_sports: number;
  total_product_views: number;
  created_at: string;
  updated_at: string;
}

export interface PaywallCounterTotals {
  users: number;
  paywall_listener: number;
  single_game: number;
  football: number;
  basketball: number;
  all_sports: number;
}

export interface PaywallCounterResponse {
  success: boolean;
  rows: PaywallCounterRow[];
  count: number;
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
  totals: PaywallCounterTotals;
  error?: string;
}

export interface OpsPaywallCounterParams {
  q?: string;
  sort?: string;
  offset?: number;
  limit?: number;
}

export const opsPaywallCounterApi = {
  async list(params: OpsPaywallCounterParams = {}): Promise<ApiResponse<PaywallCounterResponse>> {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.sort) query.set('sort', params.sort);
    if (params.offset != null) query.set('offset', String(params.offset));
    if (params.limit != null) query.set('limit', String(params.limit));
    const qs = query.toString();
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/paywall-counter${qs ? `?${qs}` : ''}`);
    return handleResponse(response);
  },
};

export interface ContactCounterRow {
  user_id: string;
  email: string;
  contact_visit: number;
  contact_submitted: number;
  created_at: string;
  updated_at: string;
}

export interface ContactCounterTotals {
  users: number;
  contact_visit: number;
  contact_submitted: number;
}

export interface ContactCounterResponse {
  success: boolean;
  rows: ContactCounterRow[];
  count: number;
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
  totals: ContactCounterTotals;
  error?: string;
}

// Same query params as the paywall counter (same table, contact_* columns).
export const opsContactCounterApi = {
  async list(params: OpsPaywallCounterParams = {}): Promise<ApiResponse<ContactCounterResponse>> {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.sort) query.set('sort', params.sort);
    if (params.offset != null) query.set('offset', String(params.offset));
    if (params.limit != null) query.set('limit', String(params.limit));
    const qs = query.toString();
    const response = await fetch(`${ADMIN_API_BASE_URL}/ops/contact-counter${qs ? `?${qs}` : ''}`);
    return handleResponse(response);
  },
};

const apiClient = {
  imageMatching: imageMatchingApi,
  liveUrlGameIndexing: liveUrlGameIndexingApi,
  scheduler: schedulerApi,
  games: gamesApi,
  venueBotMappings: venueBotMappingApi,
  teams: teamApi,
  adminTeams: adminTeamApi,
  firebaseAnalytics: firebaseAnalyticsApi,
  mockGenius: mockGeniusApi,
  products: productApi,
  manualNotifications: manualNotificationsApi,
  opsSync: opsSyncApi,
  opsHealth: opsHealthApi,
  opsInfrastructure: opsInfrastructureApi,
  opsApiHealth: opsApiHealthApi,
  opsManual: opsManualApi,
  opsPurchasers: opsPurchasersApi,
  opsPurchaseTimeline: opsPurchaseTimelineApi,
  opsPaywallCounter: opsPaywallCounterApi,
  opsContactCounter: opsContactCounterApi,
};

export default apiClient;
